import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase/firebase-config';
import SEO from '../components/SEO';
import {
  getWorkspace, getContract, deleteContract, correctFact,
  listContractTasks, createTask, setTaskStatus, updateTaskDueDate, deleteTask,
} from '../utils/deskApi';
import ppLogo from '../assets/images/pp-logo.png';
import '../Styles/Desk.css';

function FactRow({ contractId, workspaceId, factKey, text, page, correction, onCorrected, category, dueDate, tracked, onTrack }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const [busy, setBusy] = useState(false);
  const [tracking, setTracking] = useState(false);

  const act = async (status, editedText) => {
    setBusy(true);
    try {
      const { correction: saved } = await correctFact(workspaceId, contractId, factKey, status, editedText, '');
      onCorrected(factKey, saved);
      setEditing(false);
    } catch (_) { /* silently keep prior state — the button re-enables for retry */ }
    setBusy(false);
  };

  const track = async () => {
    setTracking(true);
    await onTrack({ factKey, text, category, dueDate, page });
    setTracking(false);
  };

  const displayText = correction?.status === 'edited' && correction.editedText ? correction.editedText : text;

  return (
    <li className={`desk-fact${correction ? ` is-${correction.status}` : ''}`}>
      {editing ? (
        <div className="desk-fact-edit">
          <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={2} />
          <div>
            <button type="button" disabled={busy} onClick={() => act('edited', draft)}>Save</button>
            <button type="button" className="ghost" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <span className="desk-fact-text">{displayText}{page ? <em className="desk-page-badge">p. {page}</em> : null}</span>
          <span className="desk-fact-actions">
            {correction?.status === 'confirmed' && <span className="desk-fact-status">✓ Confirmed</span>}
            {correction?.status === 'edited' && <span className="desk-fact-status">✎ Edited</span>}
            {correction?.status === 'flagged' && <span className="desk-fact-status">⚑ Flagged for review</span>}
            {!correction && (
              <>
                <button type="button" disabled={busy} title="Confirm as correct" onClick={() => act('confirmed', null)}>✓</button>
                <button type="button" disabled={busy} title="Edit" onClick={() => { setDraft(text); setEditing(true); }}>✎</button>
                <button type="button" disabled={busy} title="Flag for review" onClick={() => act('flagged', null)}>⚑</button>
              </>
            )}
            {tracked ? (
              <span className="desk-fact-status" title="Already tracked as a task">📌 Tracked</span>
            ) : (
              <button type="button" disabled={tracking} title="Track as a task" onClick={track}>📌</button>
            )}
          </span>
        </>
      )}
    </li>
  );
}

function TasksPanel({ workspaceId, contractId, tasks, onToggle, onDueDateChange, onDelete, onAdd }) {
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const submitAdd = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setAdding(true);
    await onAdd({ text, category: 'custom' });
    setDraft('');
    setAdding(false);
  };

  const open = tasks.filter(t => t.status === 'open');
  const done = tasks.filter(t => t.status === 'done');
  const sorted = [
    ...open.sort((a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999')),
    ...done,
  ];

  return (
    <div className="desk-card desk-tasks-card">
      <h2>Tasks {open.length > 0 && <span className="desk-task-count">{open.length} open</span>}</h2>
      {sorted.length === 0 ? (
        <p className="desk-tasks-empty">No tasks yet — track a fact above, or add one below.</p>
      ) : (
        <ul className="desk-task-list">
          {sorted.map(t => (
            <li key={t.id} className={t.status === 'done' ? 'is-done' : (t.dueDate && t.dueDate < today ? 'is-overdue' : '')}>
              <input
                type="checkbox"
                checked={t.status === 'done'}
                onChange={e => onToggle(t.id, e.target.checked)}
              />
              <span className="desk-task-text">{t.text}{t.page ? <em className="desk-page-badge">p. {t.page}</em> : null}</span>
              <input
                type="date"
                value={t.dueDate || ''}
                onChange={e => onDueDateChange(t.id, e.target.value)}
                aria-label="Due date"
              />
              <button type="button" className="desk-task-remove" onClick={() => onDelete(t.id)} aria-label="Delete task">×</button>
            </li>
          ))}
        </ul>
      )}
      <form className="desk-task-add" onSubmit={submitAdd}>
        <input type="text" placeholder="Add a custom task…" value={draft} onChange={e => setDraft(e.target.value)} />
        <button type="submit" disabled={adding || !draft.trim()}>{adding ? 'Adding…' : '+ Add'}</button>
      </form>
    </div>
  );
}

export default function DeskContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [workspace, setWorkspace] = useState(null);
  const [contract, setContract] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (u) => {
    setUser(u || null);
    if (!u) navigate('/desk/login');
  }), [navigate]);

  const load = useCallback(async () => {
    setError('');
    try {
      const { workspace: ws } = await getWorkspace();
      setWorkspace(ws);
      const [{ contract: c }, { tasks: t }] = await Promise.all([
        getContract(ws.id, id),
        listContractTasks(ws.id, id),
      ]);
      setContract(c);
      setTasks(t);
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const onCorrected = (factKey, correction) => {
    setContract(prev => ({ ...prev, corrections: { ...prev.corrections, [factKey]: correction } }));
  };

  const trackedFactKeys = new Set(tasks.map(t => t.factKey).filter(Boolean));

  const addTask = async ({ factKey, text, category, dueDate }) => {
    try {
      const { task } = await createTask(workspace.id, contract.id, { factKey, text, category, dueDate });
      setTasks(prev => [...prev, task]);
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleTask = async (taskId, done) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: done ? 'done' : 'open' } : t));
    try {
      await setTaskStatus(workspace.id, contract.id, taskId, done);
    } catch (err) {
      setError(err.message);
    }
  };

  const changeTaskDueDate = async (taskId, dueDate) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, dueDate: dueDate || null } : t));
    try {
      await updateTaskDueDate(workspace.id, contract.id, taskId, dueDate);
    } catch (err) {
      setError(err.message);
    }
  };

  const removeTask = async (taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
    try {
      await deleteTask(workspace.id, contract.id, taskId);
    } catch (err) {
      setError(err.message);
    }
  };

  const removeContract = async () => {
    if (!window.confirm(`Delete "${contract.filename}"? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await deleteContract(workspace.id, contract.id);
      navigate('/desk');
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  if (user === undefined || (!contract && !error)) {
    return <div className="desk-loading"><span /><span /><span /></div>;
  }

  const a = contract?.analysis || {};
  const corrections = contract?.corrections || {};
  const hasAnyOf = (arr) => Array.isArray(arr) && arr.length > 0;

  return (
    <>
      <SEO title={`${contract?.filename || 'Contract'} — Contract Desk`} description="Contract detail." noIndex />
      <div className="desk-app">
        <header className="desk-topbar">
          <Link to="/" className="desk-logo"><img src={ppLogo} alt="The PP Studio" /><em>Contract Desk</em></Link>
          <div className="desk-topbar-right">
            <button type="button" onClick={() => signOut(auth)}>Sign out</button>
          </div>
        </header>

        <main className="desk-main">
          <Link to="/desk" className="desk-back-link">← All contracts</Link>

          {error && <p className="desk-error">{error}</p>}

          {contract && (
            <>
              <div className="desk-main-head">
                <div>
                  <h1>{contract.filename}</h1>
                  <p>{contract.pageCount ? `${contract.pageCount} pages · ` : ''}Uploaded {contract.uploadedAt?._seconds ? new Date(contract.uploadedAt._seconds * 1000).toLocaleDateString('en-GB') : 'recently'}</p>
                </div>
                <button type="button" className="desk-delete-btn" disabled={deleting} onClick={removeContract}>
                  {deleting ? 'Removing…' : 'Delete contract'}
                </button>
              </div>

              <TasksPanel
                workspaceId={workspace.id}
                contractId={contract.id}
                tasks={tasks}
                onToggle={toggleTask}
                onDueDateChange={changeTaskDueDate}
                onDelete={removeTask}
                onAdd={addTask}
              />

              <div className="desk-card">
                <h2>The agreement at a glance</h2>
                <dl>
                  {a.deal_brief?.parties && <><dt>Parties</dt><dd>{a.deal_brief.parties}</dd></>}
                  {a.deal_brief?.purpose && <><dt>Purpose</dt><dd>{a.deal_brief.purpose}</dd></>}
                  {a.deal_brief?.value && <><dt>Value</dt><dd>{a.deal_brief.value}</dd></>}
                  {a.deal_brief?.duration && <><dt>Duration</dt><dd>{a.deal_brief.duration}</dd></>}
                </dl>
              </div>

              {hasAnyOf(a.deliverables) && (
                <div className="desk-card">
                  <h2>We owe — working checklist</h2>
                  <ul className="desk-fact-list">
                    {a.deliverables.map((f, i) => (
                      <FactRow key={i} contractId={contract.id} workspaceId={workspace.id} factKey={`deliverables.${i}`}
                        text={f.text} page={f.page} category="deliverable" correction={corrections[`deliverables.${i}`]}
                        onCorrected={onCorrected} tracked={trackedFactKeys.has(`deliverables.${i}`)} onTrack={addTask} />
                    ))}
                  </ul>
                </div>
              )}

              {hasAnyOf(a.payment_schedule) && (
                <div className="desk-card">
                  <h2>Money and payment timing</h2>
                  <ul className="desk-fact-list">
                    {a.payment_schedule.map((p, i) => (
                      <FactRow key={i} contractId={contract.id} workspaceId={workspace.id} factKey={`payment_schedule.${i}`}
                        text={[p.description, p.amount, p.due].filter(Boolean).join(' — ')} page={p.page} category="payment"
                        correction={corrections[`payment_schedule.${i}`]} onCorrected={onCorrected}
                        tracked={trackedFactKeys.has(`payment_schedule.${i}`)} onTrack={addTask} />
                    ))}
                  </ul>
                </div>
              )}

              {hasAnyOf(a.key_dates) && (
                <div className="desk-card">
                  <h2>Contract timeline</h2>
                  <ul className="desk-fact-list">
                    {a.key_dates.map((d, i) => (
                      <FactRow key={i} contractId={contract.id} workspaceId={workspace.id} factKey={`key_dates.${i}`}
                        text={`${d.label}${d.detail ? ` — ${d.detail}` : ''}${d.date_iso ? ` (${d.date_iso})` : ''}`} page={d.page}
                        category="date" dueDate={d.date_iso} correction={corrections[`key_dates.${i}`]} onCorrected={onCorrected}
                        tracked={trackedFactKeys.has(`key_dates.${i}`)} onTrack={addTask} />
                    ))}
                  </ul>
                </div>
              )}

              {hasAnyOf(a.client_obligations) && (
                <div className="desk-card">
                  <h2>They owe</h2>
                  <ul className="desk-fact-list">
                    {a.client_obligations.map((f, i) => (
                      <FactRow key={i} contractId={contract.id} workspaceId={workspace.id} factKey={`client_obligations.${i}`}
                        text={f.text} page={f.page} category="obligation" correction={corrections[`client_obligations.${i}`]}
                        onCorrected={onCorrected} tracked={trackedFactKeys.has(`client_obligations.${i}`)} onTrack={addTask} />
                    ))}
                  </ul>
                </div>
              )}

              {hasAnyOf(a.attention_flags) && (
                <div className="desk-card desk-card-flags">
                  <h2>Pause and look again</h2>
                  <ul className="desk-fact-list">
                    {a.attention_flags.map((f, i) => (
                      <FactRow key={i} contractId={contract.id} workspaceId={workspace.id} factKey={`attention_flags.${i}`}
                        text={f.text} page={f.page} correction={corrections[`attention_flags.${i}`]} onCorrected={onCorrected} />
                    ))}
                  </ul>
                </div>
              )}

              {hasAnyOf(a.questions_to_raise) && (
                <div className="desk-card">
                  <h2>Questions worth asking</h2>
                  <ul>
                    {a.questions_to_raise.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
